import React, { Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import store from './store';
import './styles/global.css';

// Layout (eager — used on every page)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import DeliveryRoute from './components/auth/DeliveryRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import { RegionProvider } from './utils/RegionContext';
import Loading from './components/common/Loading';
import InstallPrompt from './components/common/InstallPrompt';

// Route-level code splitting — each page is a separate chunk
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const CartPage = lazy(() => import('./pages/CartPage'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const MyOrders = lazy(() => import('./pages/MyOrders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const Profile = lazy(() => import('./pages/Profile'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Admin chunks (only loaded for admins)
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./components/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./components/admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('./components/admin/AdminOrderDetail'));
const AdminProductForm = lazy(() => import('./components/admin/AdminProductForm'));
const AdminUsers = lazy(() => import('./components/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./components/admin/AdminUserDetail'));
const AdminAuditLog = lazy(() => import('./components/admin/AdminAuditLog'));
const AdminCodReconciliation = lazy(() => import('./components/admin/AdminCodReconciliation'));
const AdminAnalytics = lazy(() => import('./components/admin/AdminAnalytics'));

// Delivery-partner chunks (mobile-first operational surface)
const DeliveryLayout = lazy(() => import('./pages/delivery/DeliveryLayout'));
const DeliveryAssignedOrders = lazy(() => import('./pages/delivery/DeliveryAssignedOrders'));
const DeliveryOrderDetail = lazy(() => import('./pages/delivery/DeliveryOrderDetail'));

function App() {
  return (
    <Provider store={store}>
      <ErrorBoundary>
        <RegionProvider>
          <Router>
          <div className="app">
            {/* Skip-link: hidden until focused, lets keyboard users jump past nav */}
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <Header />
            <main
              id="main-content"
              tabIndex={-1}
              style={{
                minHeight: 'calc(100vh - var(--header-height) - 300px)',
                paddingTop: 'var(--header-height)',
              }}
            >
              <Suspense fallback={<Loading />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/shop/:type" element={<Shop />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/contact" element={<Contact />} />

                  {/* Protected Routes */}
                  <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
                  <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                  <Route path="/order-success/:id" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
                  <Route path="/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
                  <Route path="/orders/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
                  <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                  <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
                  <Route path="/admin/products/new" element={<AdminRoute><AdminProductForm /></AdminRoute>} />
                  <Route path="/admin/products/edit/:id" element={<AdminRoute><AdminProductForm /></AdminRoute>} />
                  <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
                  <Route path="/admin/orders/:id" element={<AdminRoute><AdminOrderDetail /></AdminRoute>} />
                  <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                  <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
                  <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
                  <Route path="/admin/cod-reconciliation" element={<AdminRoute><AdminCodReconciliation /></AdminRoute>} />
                  <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />

                  {/* Delivery Partner Routes */}
                  <Route path="/delivery" element={<DeliveryRoute><DeliveryLayout /></DeliveryRoute>}>
                    <Route index element={<DeliveryAssignedOrders />} />
                    <Route path="orders/:id" element={<DeliveryOrderDetail />} />
                  </Route>

                  {/* 404 fallback */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
          <InstallPrompt />
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </Router>
        </RegionProvider>
      </ErrorBoundary>
    </Provider>
  );
}

export default App;
